import type { ReactElement, ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface SidebarNavItemProps {
  /** Slot do ícone (Tabler outline stroke 1.7) */
  icon: ReactNode;
  /** Label do item */
  label: string;
  /** Caminho href (pra Link do framework) */
  href: string;
  /** Render-prop: filho que renderiza o link real (Next Link) com a className calculada */
  children: (props: { className: string }) => ReactNode;
  /** Item ativo (highlight violeta) */
  active?: boolean;
  /** Item desabilitado (em construção) */
  disabled?: boolean;
  /** Badge opcional (ex: "novo", contagem) */
  badge?: ReactNode;
}

export function SidebarNavItem({
  icon,
  label,
  active,
  disabled,
  badge,
  children,
}: SidebarNavItemProps): ReactElement {
  const className = cn(
    'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
    active
      ? 'bg-color-violet/[0.14] text-color-violet before:absolute before:left-0 before:top-1/2 before:h-6 before:w-[3px] before:-translate-y-1/2 before:rounded-r before:bg-color-violet before:shadow-[0_0_12px_oklch(0.62_0.22_295/0.8)]'
      : disabled
        ? 'text-ink-faint cursor-not-allowed'
        : 'text-ink-muted hover:bg-surface-elevated/60 hover:text-ink',
  );

  if (disabled) {
    return (
      <span aria-disabled className={className}>
        <span className="shrink-0">{icon}</span>
        <span className="flex-1 truncate">{label}</span>
        {badge ? <span className="shrink-0">{badge}</span> : null}
      </span>
    );
  }

  return <>{children({ className })}</>;
}

export interface SidebarNavSectionProps {
  /** Label do grupo (ex: "Análise") */
  label?: string;
  children: ReactNode;
}

export function SidebarNavSection({ label, children }: SidebarNavSectionProps): ReactElement {
  return (
    <div className="space-y-1">
      {label ? (
        <p className="text-ink-faint px-3 pt-3 pb-1 font-mono text-[10px] tracking-[0.14em] uppercase">
          {label}
        </p>
      ) : null}
      {children}
    </div>
  );
}
